import { type JSX } from 'react'
import { Input } from '@ttab/elephant-ui'

/**
 * The memo's heading, which every template has one of.
 *
 * It sits in the preview, in the place of the heading it will be, rather than
 * among the fields above: the preview is where the heading is read, and a memo
 * that is only a headline and a body typed in afterwards is a memo whose
 * headline is the whole of what the dialog is for.
 *
 * So it is styled as that heading rather than as a form field - the weight and
 * the size `PreviewLine` gives a `heading-1` - and keeps only the border that
 * says it can be typed in. An empty one is a memo that cannot be created, which
 * it says the way the required fields above do: by tinting its placeholder and
 * its border while nothing has been written.
 */
const HEADING_CLASS = `h-8 mb-1 text-base font-bold bg-background
  placeholder:font-normal placeholder:text-[#5D709F] placeholder-shown:border-[#5D709F]
  read-only:border-transparent read-only:shadow-none`

export const PreviewHeading = ({ label, value, onValueChange, readOnly }: {
  /** Names the field to a screen reader, and stands in for the empty heading. */
  label: string
  value: string
  onValueChange: (value: string) => void
  readOnly?: boolean
}): JSX.Element => (
  <Input
    className={HEADING_CLASS}
    aria-label={label}
    placeholder={label}
    readOnly={readOnly}
    value={value}
    onChange={(event) => onValueChange(event.target.value)}
  />
)
